<template>
  <div
    class="custom-audio-player flex flex-col items-center w-full max-w-md mx-auto font-sans"
  >
    <audio
      ref="audioPlayer"
      :src="src || ''"
      :loop="loop"
      @play="handlePlay"
      @pause="handlePause"
      @ended="handleEnded"
      @timeupdate="handleTimeUpdate"
      @loadedmetadata="handleLoadedMetadata"
      @durationchange="handleLoadedMetadata"
      style="display: none"
    ></audio>

    <div class="w-full text-center text-xs text-black mb-1">
      <span>{{ formattedCurrentTime }}</span> -
      <span>{{ formattedDuration }}</span>
    </div>

    <div class="w-full px-1 group">
      <input
        type="range"
        :value="progress"
        @input="seekAudio"
        min="0"
        max="100"
        step="0.1"
        class="slider-progress w-full h-2 rounded-lg appearance-none cursor-pointer focus:outline-none"
        :style="{
          background: `linear-gradient(to right, var(--p-primary-900) 0%, var(--p-primary-900) ${progress}%, #e5e7eb ${progress}%, #e5e7eb 100%)`,
        }"
        :disabled="!duration"
        aria-label="Audio Progress"
      />
    </div>

    <div class="flex items-center justify-around w-full">
      <button
        @click="skipTime(-10)"
        class="p-2 text-black hover:!text-primary-500 disabled:!text-gray-400 disabled:cursor-not-allowed transition-colors duration-150"
        :disabled="!duration"
        aria-label="Skip Backward 10 seconds"
        title="Skip Backward 10s"
      >
        <v-icon>mdi-rewind-outline</v-icon>
      </button>

      <button
        @click="togglePlayPause"
        class="p-2 mx-2 text-black hover:!text-primary-500 disabled:!text-gray-400 disabled:cursor-not-allowed transition-colors duration-150"
        :disabled="!src"
        :aria-label="isPlaying ? 'Pause' : 'Play'"
      >
        <v-icon v-if="!isPlaying">mdi-play-outline</v-icon>
        <v-icon v-else>mdi-pause</v-icon>
      </button>

      <button
        @click="skipTime(10)"
        class="p-2 text-black hover:!text-primary-500 disabled:!text-gray-400 disabled:cursor-not-allowed transition-colors duration-150"
        :disabled="!duration"
        aria-label="Skip Forward 10 seconds"
        title="Skip Forward 10s"
      >
        <v-icon>mdi-fast-forward-outline</v-icon>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "CustomAudioPlayer",
  props: {
    src: {
      type: [String, null],
      default: null,
    },
    autoplay: {
      type: Boolean,
      default: false,
    },
    loop: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      currentVolume: this.initialVolume,
      isMuted: false,
      progress: 0,
    };
  },
  computed: {
    formattedCurrentTime() {
      return this.formatTime(this.currentTime);
    },
    formattedDuration() {
      return this.duration > 0 ? this.formatTime(this.duration) : "00:00";
    },
  },
  methods: {
    handlePlay() {
      this.isPlaying = true;
    },
    handlePause() {
      this.isPlaying = false;
    },
    handleEnded() {
      this.isPlaying = false;
      if (!this.loop) {
        this.currentTime = 0;
        this.progress = 0;
        if (this.$refs.audioPlayer) this.$refs.audioPlayer.currentTime = 0;
      }
    },
    handleTimeUpdate() {
      const audio = this.$refs.audioPlayer;
      if (!audio) return;
      this.currentTime = audio.currentTime;
      this.progress =
        this.duration > 0 ? (audio.currentTime / this.duration) * 100 : 0;
    },
    handleLoadedMetadata() {
      const audio = this.$refs.audioPlayer;
      if (!audio) return;
      this.duration = audio.duration;
      if (this.autoplay && audio.paused) {
        audio.play().catch((e) => console.warn("Autoplay prevented:", e));
      }
    },
    togglePlayPause() {
      const audio = this.$refs.audioPlayer;
      if (!audio || !this.src) return; // Don't operate if no src
      if (audio.paused || audio.ended) {
        audio.play().catch((e) => console.error("Play error:", e));
      } else {
        audio.pause();
      }
    },
    skipTime(amount) {
      const audio = this.$refs.audioPlayer;
      if (!audio || !this.duration) return;
      const newTime = audio.currentTime + amount;
      audio.currentTime = Math.max(0, Math.min(newTime, this.duration));
    },
    seekAudio(event) {
      const audio = this.$refs.audioPlayer;
      if (!audio || !this.duration) return;
      audio.currentTime =
        (parseFloat(event.target.value) / 100) * this.duration;
    },

    formatTime(seconds) {
      if (isNaN(seconds) || seconds === Infinity || seconds < 0) return "00:00";
      const minutes = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${minutes < 10 ? "0" : ""}${minutes}:${
        secs < 10 ? "0" : ""
      }${secs}`;
    },
    initializePlayer() {
      const audio = this.$refs.audioPlayer;
      if (audio) {
        if (audio.readyState >= 1) this.handleLoadedMetadata();
      }
    },
  },
  mounted() {
    this.initializePlayer();
  },
  beforeUnmount() {
    const audio = this.$refs.audioPlayer;
    if (audio) {
      audio.pause();
      audio.src = "";
    }
  },
  watch: {
    src(newSrc, oldSrc) {
      if (newSrc !== oldSrc) {
        this.isPlaying = false;
        this.currentTime = 0;
        this.duration = 0;
        this.progress = 0;
        this.$nextTick(() => {
          const audio = this.$refs.audioPlayer;
          if (audio) {
            audio.load();
            this.initializePlayer();
          }
        });
      }
    },
  },
};
</script>

<style scoped>
/* Progress Bar Thumb: Hidden by default, visible on group-hover/focus */
.slider-progress::-webkit-slider-thumb {
  -webkit-appearance: none; /* Essential */
  appearance: none;
  width: 1rem; /* 16px */
  height: 1rem; /* 16px */
  background-color: red !important;
  border-radius: 9999px; /* rounded-full */
  cursor: pointer;
  margin-top: -6px; /* (thumbHeight - trackHeight)/-2 : (16px - 8px)/-2 = -4px. For h-2 track (8px) and 1rem thumb (16px) */
  opacity: 0;
  transition: opacity 0.15s ease-in-out;
}

.slider-progress::-moz-range-thumb {
  width: 1rem;
  height: 1rem;
  background-color: var(--p-primary-900);
  border-radius: 9999px;
  cursor: pointer;
  border: none;
  opacity: 1;
  transition: opacity 0.15s ease-in-out;
}

/* Volume Slider Thumb: Always visible */
.slider-volume::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 0.75rem; /* 12px */
  height: 0.75rem; /* 12px */
  background-color: #2563eb; /* theme('colors.blue.600') */
  border-radius: 9999px;
  cursor: pointer;
  margin-top: -4px; /* For h-1 track (4px) and 0.75rem thumb (12px): (12-4)/-2 = -4px */
}
.slider-volume::-moz-range-thumb {
  width: 0.75rem;
  height: 0.75rem;
  background-color: #2563eb;
  border-radius: 9999px;
  cursor: pointer;
  border: none;
}
</style>
